import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch } from '@/modules/core/composables/useFetch'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'

interface Workspace {
  id: number
  name: string
  subdomain: string
  phone: string | null
  time_zone: string
  default_currency_code: string
  setup_mode: boolean
  logo_url: string | null
  is_registered_investment_advisor: boolean
  central_registration_depository_number: string | null
  registered_investment_advisor_verified_at: string | null
  billing_contact_id: number | null
  registered_investment_advisor_verified_by_name: string | null
  accreditation_country_code: string | null
  accreditation_level_min: string | null
  is_investment_group: boolean
  has_active_subscription: boolean
  connection_profile_complete: boolean
}

interface Settings {
  deal_pipeline_enabled: boolean
  secondary_deal_pipeline_enabled: boolean
  funding_application_enabled: boolean
  accepts_shared_deals: boolean
  public_investor_registration_enabled: boolean
  hide_investors_from_investors: boolean
  show_chapters_to_investors: boolean
  skip_self_accreditation: boolean
  send_investor_registration_reminders: boolean
  company_index_enabled: boolean
  company_discussions_enabled: boolean
  all_investors_view_captable: boolean
  investor_aml_verification_enabled: boolean
  connections_matching_enabled: boolean
  connections_digest_enabled: boolean
  send_past_due_invoice_reminders: boolean
  send_past_due_signature_reminders: boolean
  sort_discussion_newest_to_oldest: boolean
  link_to_email_attachments: boolean
  notify_when_investor_registers: boolean
  notify_when_company_applies: boolean
  notify_when_company_begins: boolean
  default_user_id: number | null
  custom_messages: Record<string, string>
}

interface TimezoneOption {
  value: string
  label: string
}

interface AccreditationCountryOption {
  value: string
  label: string
}

interface GroupAdminOption {
  id: number
  name: string
}

interface Options {
  timezones: TimezoneOption[]
  currencies: string[]
  accreditation_countries: AccreditationCountryOption[]
  group_admins: GroupAdminOption[]
}

interface MembershipAgreement {
  id: number
  filename: string
  url: string
  created_at: string
}

interface WorkspaceResponse {
  group: Workspace
  settings: Settings
  options: Options
  membership_agreement: MembershipAgreement | null
}

export const useWorkspaceSettingsStore = defineStore('workspace/settingsStore', () => {
  const route = useRoute()
  const notificationStore = useNotificationStore()

  const baseUrl = computed(() => `/v4/${route.params.slug}/workspace`)

  // State
  const workspace = ref<Workspace | null>(null)
  const settings = ref<Settings | null>(null)
  const options = ref<Options | null>(null)
  const membershipAgreement = ref<MembershipAgreement | null>(null)
  const loading = ref(false)

  // Actions
  const fetch = async () => {
    loading.value = true
    try {
      const data = await useFetch(baseUrl.value).get<WorkspaceResponse>()
      workspace.value = data.group
      settings.value = data.settings
      options.value = data.options
      membershipAgreement.value = data.membership_agreement
      return true
    } catch (error) {
      notificationStore.enqueue('error', 'Failed to load workspace settings')
      return false
    } finally {
      loading.value = false
    }
  }

  function jsonToFormData(jsonObject: Record<string, unknown>) {
    const formData = new FormData()

    Object.keys(jsonObject).forEach((key) => {
      const value = jsonObject[key]
      if (value !== null && value !== undefined) {
        if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'object' && value !== null) {
          formData.append(key, JSON.stringify(value))
        } else {
          formData.append(key, String(value))
        }
      }
    })

    return formData
  }

  const updateWorkspace = async (payload: Record<string, unknown>, { silent = false } = {}) => {
    loading.value = true
    try {
      const formData = jsonToFormData(payload)
      await useFetch(baseUrl.value).post(formData)
      await fetch()
      if (!silent) {
        notificationStore.enqueue('success', 'Settings saved')
      }
      return true
    } catch (error) {
      notificationStore.enqueue('error', 'Failed to update workspace')
      return false
    } finally {
      loading.value = false
    }
  }

  const updateSettings = async (payload: Partial<Settings>, { silent = false } = {}) => {
    loading.value = true
    try {
      await useFetch(`${baseUrl.value}/update_settings`).post(payload)
      await fetch()
      if (!silent) {
        notificationStore.enqueue('success', 'Settings saved')
      }
      return true
    } catch (error) {
      notificationStore.enqueue('error', 'Failed to update settings')
      return false
    } finally {
      loading.value = false
    }
  }

  const deleteWorkspace = async (password: string): Promise<{ success: boolean; error?: string }> => {
    loading.value = true
    try {
      await useFetch(`${baseUrl.value}/destroy`).post({ password })
      return { success: true }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete workspace'
      return { success: false, error: errorMessage }
    } finally {
      loading.value = false
    }
  }

  const sendRegistrationInvitations = async (): Promise<boolean> => {
    loading.value = true
    try {
      await useFetch(`${baseUrl.value}/send_registration_invitations`).post()
      notificationStore.enqueue('success', 'Registration invitations sent successfully')
      return true
    } catch (error) {
      notificationStore.enqueue('error', 'Failed to send registration invitations')
      return false
    } finally {
      loading.value = false
    }
  }

  const checkSubdomain = async (
    subdomain: string,
  ): Promise<{ available: boolean; suggestion?: string; error?: string }> => {
    try {
      const data = await useFetch(`${baseUrl.value}/check-subdomain?slug_check=${encodeURIComponent(subdomain)}`).get<{
        available: boolean
        suggestion?: string
        error?: string
      }>()
      return data
    } catch {
      return { available: false, error: 'Unable to check subdomain' }
    }
  }

  return {
    workspace,
    settings,
    options,
    membershipAgreement,
    loading,
    fetch,
    checkSubdomain,
    updateWorkspace,
    updateSettings,
    deleteWorkspace,
    sendRegistrationInvitations,
  }
})
